import { useEffect, useMemo, useState } from "react";
import SettingService from "api/services/SettingService";
import { useTranslation } from "react-i18next";
import CardTemplateIMG from 'assets/images/card-template-fb.png'
import SectionBanner from "components/cards/SectionBanner";
import { NavLink, useHistory, useRouteMatch } from "react-router-dom";
import { BiEditAlt } from 'react-icons/bi'
import OnDeleteConfirm from 'components/modals/OnDeleteConfirm'
import SectionWrapper from "components/wrappers/SectionWrapper";
import TableContainer from "components/TableContainer";
import MenuOptionDetails from "./MenuOptionDetails";
import { getLanguage } from "utils/Utils";
import PageWrapper from "components/wrappers/PageWrapper";
import ToastAlert from "components/ToastAlert";
import DashboardWrapper from "components/wrappers/DashboardWrapper";
import PropTypes from 'prop-types';

function MenuTitleCell({ data, row, column }) {
  return (
    <div className="flex-1 text-sm">
      {data[row.id][column.languageKey]?.title}
    </div>
  );
}
function MenuEnabledCell({ value }) {
  const { t } = useTranslation();
  return (
    <div className="flex-grow-0 text-sm">
      {value ?
        <span className="bg-green-50 text-green-600 px-2 py-1 rounded border border-green-500">{t('actions.enabled')}</span>
        : <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded border border-gray-200">{t('actions.disabled')}</span>
      }
    </div>
  );
}
function MenuActionsHeader() {
  const { t } = useTranslation();
  return <div className="flex justify-end uppercase px-2">{t('table.actions')}</div>;
}
function MenuActionsCell({ data, row, column }) {
  const id = data[row.id]?.id;
  const menu = data[row.id];
  return (
    <div className="flex justify-end">
      <NavLink exact
        to={`${column.url}/edit/${id}`}
        className="block text-xl text-gray-500 mx-1 p-2 rounded hover:bg-gray-100 cursor-pointer">
        <BiEditAlt />
      </NavLink>
      <OnDeleteConfirm onConfirm={() => column.onDelete(id)} key={"d3-1"} />
      <MenuOptionDetails data={menu} />
    </div>
  );
}

MenuTitleCell.propTypes = {
  data: PropTypes.array,
  row: PropTypes.shape({ id: PropTypes.string }),
  column: PropTypes.shape({ languageKey: PropTypes.string }),
};
MenuEnabledCell.propTypes = {
  value: PropTypes.bool,
};
MenuActionsCell.propTypes = {
  data: PropTypes.array,
  row: PropTypes.shape({ id: PropTypes.string }),
  column: PropTypes.shape({ url: PropTypes.string, onDelete: PropTypes.func }),
};

const MainMenu = () => {
  const { t, i18n } = useTranslation();
  const [remoteData, setRemoteData] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [deleteMenuStatus, setDeleteMenuStatus] = useState(null)
  let { url } = useRouteMatch();
  const cachedData = useMemo(() => (remoteData), [remoteData]);
  const history = useHistory()

  const onDelete = (id) => {
    SettingService.deleteMenu(id).then((resp) => {
      history.go();
      setDeleteMenuStatus("success")
    }).catch((err) => {
      //Handle error ..
      setDeleteMenuStatus("error")

    });
  }

  const columns = useMemo(

    () => [
      {
        Header: t('form.menu'),
        accessor: "menu",
        disableFilters: true,
        languageKey: getLanguage(i18n.language),
        Cell: MenuTitleCell,
      },
      {
        Header: t('form.status'),
        accessor: "enabled",
        disableFilters: true,
        Cell: MenuEnabledCell,
      },
      {
        Header: MenuActionsHeader,
        disableSortBy: true,
        accessor: "actions",
        url,
        onDelete,
        Cell: MenuActionsCell,
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  useEffect(() => {
    SettingService.getMenuList().then((resp) => {
      setRemoteData(resp)
      setLoading(false)
    }).catch((err) => {
      //Handle error ..
      setLoading(false)
      setError(err.message)
    });
  }, []);



  return (
    <DashboardWrapper>
      <SectionBanner
        title={t('main_menu.title')}
        desc={
          <div className='mt-4'>
            <div className="font-medium text-sm text-gray-800 mb-1">{t('main_menu.guide.title')}</div>
            <ul className="list-inside  text-xs font-medium">
              <li className='py-1'>{t('main_menu.guide.details.image')}</li>
              <li className='py-1'>{t('main_menu.guide.details.title')}</li>
              <li className='py-1'>{t('main_menu.guide.details.subtitle')}</li>
              <li className='py-1'>{t('main_menu.guide.details.btn_label')}</li>
            </ul>
          </div>
        }
      >
        <img src={CardTemplateIMG} alt='' height='' width='256px' />


      </SectionBanner>

      <SectionWrapper
        title={t('main_menu.main_menu_list')}
        actions={cachedData?.length > 2 ?
          <div className='capitalize bg-gray-200 text-gray-500 rounded  px-4 py-2 text-sm pointer-events-none'>
            + {t('actions.new_menu_option')}
          </div>
          :
          <NavLink exact to={`${url}/new`}
            className='capitalize bg-blue-500 text-white rounded shadow px-4 py-2 text-sm hover:bg-blue-600'>
            + {t('actions.new_menu_option')}
          </NavLink>
        }
        className="bg-white"

      >
        <PageWrapper error={error} loading={loading} >
          <TableContainer data={Array.isArray(cachedData) ? cachedData : []} columns={columns} globalSearch={true} />
        </PageWrapper>
      </SectionWrapper>

      {deleteMenuStatus === "success" && <ToastAlert message={t('alert.success_operation')} type={'success'} timeOut={5000} />}
      {deleteMenuStatus === "error" && <ToastAlert message={t('alert.failed_operation')} type={'error'} timeOut={5000} />}

    </DashboardWrapper>
  );


}

export default MainMenu;

